/**
 * Invoice / bill summary for approval list cards (ApprovalCard, ApprovalViewCard).
 * Current values come from raw_json; when original_json exists and differs, show prior struck through.
 */

import { getEffectiveTotalFromRawJson } from "../../../../shared/utils/review/invoiceBillEffectiveTotal";
import {
  formatReverseChargeAmountWithDocument,
  getReverseChargeContextFromJson,
  resolveReverseChargeAmount,
} from "../../../../shared/utils/reverseCharge";

export type InvoiceBillCardKind = "invoice" | "bill";

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v != null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

/** Proposed / current document (always prefer for display). */
export function getInvoiceBillRawJson(
  changeDetails: { raw_json?: unknown } | null | undefined,
): Record<string, unknown> | null {
  return asRecord(changeDetails?.raw_json);
}

/** Prior document for diff / strike-through (optional). */
export function getInvoiceBillOriginalJson(
  changeDetails: { original_json?: unknown } | null | undefined,
): Record<string, unknown> | null {
  return asRecord(changeDetails?.original_json);
}

/**
 * @deprecated Prefer getInvoiceBillRawJson for “current” snapshot; kept for any legacy call sites.
 */
export function getInvoiceBillCardSnapshotJson(
  changeDetails: { original_json?: unknown; raw_json?: unknown } | null | undefined,
): Record<string, unknown> | null {
  const raw = getInvoiceBillRawJson(changeDetails);
  if (raw) return raw;
  return getInvoiceBillOriginalJson(changeDetails);
}

function num(v: unknown): number {
  if (v === undefined || v === null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function currencySymbol(j: Record<string, unknown>): string {
  const s = j.currency_symbol;
  return typeof s === "string" && s.length > 0 ? s : "₹";
}

export function formatInvoiceBillMoney(
  amount: number,
  symbol: string,
): string {
  return `${symbol} ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function effectiveTotal(
  j: Record<string, unknown>,
  kind: InvoiceBillCardKind,
): number {
  if (kind === "bill") {
    return getEffectiveTotalFromRawJson(j, "bill");
  }
  const stated = j.total;
  if (stated !== undefined && stated !== null && stated !== "") {
    return num(stated);
  }
  return num(j.sub_total) + num(j.tax_total);
}

function moneyEqual(a: unknown, b: unknown): boolean {
  return Math.abs(num(a) - num(b)) < 0.005;
}

/** DD-MM-YYYY for card scanability */
export function formatInvoiceBillCardDate(j: Record<string, unknown>): string {
  const raw =
    (j.date as string | undefined) ||
    (j.invoice_date as string | undefined) ||
    (j.bill_date as string | undefined);
  if (!raw || typeof raw !== "string") return "—";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return raw;
  }
}

export function getInvoiceBillPartyName(
  kind: InvoiceBillCardKind,
  j: Record<string, unknown>,
): string {
  if (kind === "invoice") {
    const n = j.customer_name;
    return typeof n === "string" && n.trim() ? n.trim() : "—";
  }
  const v =
    j.vendor_name ||
    j.vendor_legal_name ||
    j.vendor_display_name ||
    "";
  const s = typeof v === "string" ? v.trim() : "";
  return s.length > 0 ? s : "—";
}

export function getInvoiceBillReference(
  kind: InvoiceBillCardKind,
  j: Record<string, unknown>,
): string {
  if (kind === "invoice") {
    const n = j.invoice_number;
    return typeof n === "string" && n.trim() ? n.trim() : "—";
  }
  const b = j.bill_number || j.vendor_bill_number;
  const s = typeof b === "string" ? b.trim() : "";
  return s.length > 0 ? s : "—";
}

export type InvoiceBillCardDiffRow = {
  current: string;
  previous?: string;
  changed: boolean;
};

export type InvoiceBillCardAmountRows = {
  subTotal: InvoiceBillCardDiffRow;
  taxTotal: InvoiceBillCardDiffRow;
  totalAmount: InvoiceBillCardDiffRow;
  reverseChargeNote?: string;
};

export type InvoiceBillCardDiff = {
  date: InvoiceBillCardDiffRow;
  reference: InvoiceBillCardDiffRow;
  party: InvoiceBillCardDiffRow;
  amounts: InvoiceBillCardAmountRows;
};

/**
 * Build field-level diff: current strings from raw_json; previous from original_json when it differs.
 */
export function buildInvoiceBillCardDiff(
  kind: InvoiceBillCardKind,
  changeDetails: { original_json?: unknown; raw_json?: unknown } | null | undefined,
): InvoiceBillCardDiff | null {
  const raw = getInvoiceBillRawJson(changeDetails);
  if (!raw) return null;

  const orig = getInvoiceBillOriginalJson(changeDetails);
  const hasOrig = orig != null;

  const sym = currencySymbol(raw);
  const symOrig = orig ? currencySymbol(orig) : sym;

  const curDate = formatInvoiceBillCardDate(raw);
  const prevDate = hasOrig ? formatInvoiceBillCardDate(orig!) : undefined;
  const dateChanged =
    hasOrig && prevDate !== undefined && prevDate !== curDate;

  const curRef = getInvoiceBillReference(kind, raw);
  const prevRef = hasOrig ? getInvoiceBillReference(kind, orig!) : undefined;
  const refChanged =
    hasOrig && prevRef !== undefined && prevRef !== curRef;

  const curParty = getInvoiceBillPartyName(kind, raw);
  const prevParty = hasOrig ? getInvoiceBillPartyName(kind, orig!) : undefined;
  const partyChanged =
    hasOrig && prevParty !== undefined && prevParty !== curParty;

  const curSub = num(raw.sub_total);
  const curTax = num(raw.tax_total);
  const curTotal = effectiveTotal(raw, kind);

  const prevSub = hasOrig ? num(orig!.sub_total) : undefined;
  const prevTax = hasOrig ? num(orig!.tax_total) : undefined;
  const prevTotal = hasOrig ? effectiveTotal(orig!, kind) : undefined;

  const rcContext =
    kind === "bill" ? getReverseChargeContextFromJson(raw) : null;
  const reverseChargeNote =
    rcContext?.showMessage && curTax > 0
      ? `Reverse charges of ${formatReverseChargeAmountWithDocument(
          resolveReverseChargeAmount({
            taxTotal: curTax,
            documentCurrencyCode: raw.currency_code as string | undefined,
            documentCurrencySymbol: sym,
            exchangeRate: raw.exchange_rate as number | undefined,
          }),
        )} will be applied`
      : undefined;

  const subChanged =
    hasOrig &&
    prevSub !== undefined &&
    !moneyEqual(orig!.sub_total, raw.sub_total);
  const taxChanged =
    hasOrig &&
    prevTax !== undefined &&
    !moneyEqual(orig!.tax_total, raw.tax_total);
  const totalChanged =
    hasOrig &&
    prevTotal !== undefined &&
    Math.abs(prevTotal - curTotal) >= 0.005;

  return {
    date: {
      current: curDate,
      previous: dateChanged ? prevDate : undefined,
      changed: dateChanged,
    },
    reference: {
      current: curRef,
      previous: refChanged ? prevRef : undefined,
      changed: refChanged,
    },
    party: {
      current: curParty,
      previous: partyChanged ? prevParty : undefined,
      changed: partyChanged,
    },
    amounts: {
      subTotal: {
        current: formatInvoiceBillMoney(curSub, sym),
        previous:
          subChanged && prevSub !== undefined
            ? formatInvoiceBillMoney(prevSub, symOrig)
            : undefined,
        changed: subChanged,
      },
      taxTotal: {
        current: formatInvoiceBillMoney(curTax, sym),
        previous:
          taxChanged && prevTax !== undefined
            ? formatInvoiceBillMoney(prevTax, symOrig)
            : undefined,
        changed: taxChanged,
      },
      totalAmount: {
        current: formatInvoiceBillMoney(curTotal, sym),
        previous:
          totalChanged && prevTotal !== undefined
            ? formatInvoiceBillMoney(prevTotal, symOrig)
            : undefined,
        changed: totalChanged,
      },
      reverseChargeNote,
    },
  };
}
